import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ordersAPI, escrowAPI } from '../services/api';
import { useNotifications } from './NotificationContext';
import { useAuth } from './AuthContext';

const OrderContext = createContext();

export const OrderProvider = ({ children }) => {
  const { showCustomToast } = useNotifications();
  const { user } = useAuth();

  // ── State ────────────────────────────────────────────────────────
  const [orders, setOrders] = useState([]);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orderMetadata, setOrderMetadata] = useState({
    totalOrders: 0,
    activeOrders: 0,
    completedOrders: 0,
    cancelledOrders: 0,
  });

  // ── Fetch All Orders ─────────────────────────────────────────────
  const fetchOrders = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const response = await ordersAPI.getUserOrders();
      const ordersData = response?.data?.orders || response?.data?.data || response?.data || [];
      const arr = Array.isArray(ordersData) ? ordersData : [];
      setOrders(arr);
      setOrderMetadata({
        totalOrders: arr.length,
        activeOrders: arr.filter(o => ['pending', 'confirmed', 'processing', 'shipped'].includes(o.status)).length,
        completedOrders: arr.filter(o => o.status === 'delivered').length,
        cancelledOrders: arr.filter(o => o.status === 'cancelled').length,
      });
    } catch (err) {
      console.error('Failed to fetch orders:', err);
      setError(err.message || 'Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // ── Get Single Order ─────────────────────────────────────────────
  const getOrderDetails = useCallback(async (orderId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ordersAPI.getById(orderId);
      const orderData = response?.data?.order || response?.data?.data || response?.data;
      setCurrentOrder(orderData);
      return orderData;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Confirm Delivery (Step 4) ──────────────────
  const confirmDelivery = useCallback(async (orderId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ordersAPI.confirmDelivery(orderId);
      if (response?.data?.success || response?.data) {
        showCustomToast({
          type: 'success',
          title: 'Delivery Confirmed',
          message: 'Payment has been released to the seller. Thank you!',
          duration: 5000,
        });
        await fetchOrders();
        if (currentOrder?.id === orderId || currentOrder?._id === orderId) {
          await getOrderDetails(orderId);
        }
        return response.data;
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to confirm delivery';
      setError(errorMsg);
      showCustomToast({
        type: 'error',
        title: 'Confirmation Failed',
        message: errorMsg,
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchOrders, getOrderDetails, showCustomToast, currentOrder]);

  // ── Cancel Order ─────────────────────────────────────────────────
  const cancelOrder = useCallback(async (orderId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ordersAPI.cancel(orderId);
      if (response?.data?.success || response?.data) {
        showCustomToast({
          type: 'info',
          title: 'Order Cancelled',
          message: 'Your order has been cancelled.',
        });
        await fetchOrders();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to cancel order';
      setError(errorMsg);
      showCustomToast({
        type: 'error',
        title: 'Cancellation Failed',
        message: errorMsg,
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchOrders, showCustomToast]);

  const getEscrowStatus = useCallback(async (orderId) => {
    try {
      const response = await escrowAPI.getEscrowStatus(orderId);
      // apiClient returns the parsed body ({ success, message, data }), so the
      // payload is response.data — response.data.data is always undefined and
      // made this always return null.
      return response?.data ?? null;
    } catch (err) {
      if (err.status === 404 || err.message?.includes('404')) return null;
      return null;
    }
  }, []);

  const getEscrowTimeline = useCallback(async (orderId) => {
    try {
      const response = await escrowAPI.getEscrowTimeline(orderId);
      const payload = response?.data;
      return Array.isArray(payload) ? payload : (payload?.timeline ?? []);
    } catch (err) {
      return [];
    }
  }, []);

  // ── Filter Helpers ───────────────────────────────────────────────
  const getOrdersByStatus = useCallback((status) => {
    return orders.filter(order => order.status === status);
  }, [orders]);

  const getActiveOrders = useCallback(() => {
    return orders.filter(o => ['pending', 'confirmed', 'processing', 'shipped'].includes(o.status));
  }, [orders]);

  // ── Initialize on Mount ──────────────────────────────────────────
  useEffect(() => {
    if (user) fetchOrders();
  }, [user, fetchOrders]);

  // ── Context Value ────────────────────────────────────────────────
  const value = {
    // State
    orders,
    currentOrder,
    loading,
    error,
    orderMetadata,

    // Actions
    fetchOrders,
    getOrderDetails,
    confirmDelivery,
    cancelOrder,

    getEscrowStatus,
    getEscrowTimeline,

    // Queries
    getOrdersByStatus,
    getActiveOrders,
  };

  return <OrderContext.Provider value={value}>{children}</OrderContext.Provider>;
};

// ── Custom Hook ──────────────────────────────────────────────────
export const useOrder = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrder must be used within an OrderProvider');
  }
  return context;
};

export default OrderContext;
